import axios from 'axios';
import { triggerSessionExpired } from './sessionExpiry';
import {
  dispatchSubscriptionRequired,
  isSubscriptionRedirectPending,
  isSubscriptionRequiredError,
} from './subscriptionGate';

// Exact-path allowlist (normalized, no origin/query/trailing-slash) for
// requests that can legitimately 401 on bad credentials/input and must not
// trigger a session-expiry redirect.
const BYPASS_PATHS = [
  '/users/login',
  '/users/student-login',
  '/users/forgot-password',
  '/users/resetPassword',
  '/users/register-parent',
];

function normalizeRequestPath(url) {
  if (!url) return '';
  try {
    // Resolve relative URLs against the current origin so absolute and
    // relative request URLs normalize to the same pathname.
    const resolved = new URL(url, window.location.origin);
    return resolved.pathname.replace(/\/+$/, '') || '/';
  } catch (_) {
    return String(url).split('?')[0].replace(/\/+$/, '');
  }
}

function isBypassPath(url) {
  const path = normalizeRequestPath(url);
  return BYPASS_PATHS.some((bypassPath) => path.endsWith(bypassPath));
}

function getRequestAuthorizationHeader(config) {
  const headers = config?.headers || {};
  // Header casing can vary (Authorization vs authorization); check both.
  return headers.Authorization || headers.authorization || null;
}

function wasAuthenticatedRequest(error) {
  const requestAuthHeader = getRequestAuthorizationHeader(error.config);
  if (requestAuthHeader) return true;
  // Fall back to the axios default only if the request itself carried no
  // explicit header — a stale localStorage token alone (with no header on
  // the actual failed request) doesn't prove this request was authenticated.
  return Boolean(axios.defaults.headers.common['Authorization']);
}

let installed = false;

export function installAxiosSessionInterceptor() {
  if (installed) return;
  installed = true;

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      if (status === 401 && !isBypassPath(error.config?.url) && wasAuthenticatedRequest(error)) {
        triggerSessionExpired('expired');
      } else if (isSubscriptionRequiredError(error)) {
        dispatchSubscriptionRequired();
        if (isSubscriptionRedirectPending()) {
          // The page is leaving for checkout or /subscription-required (STUD-631). Never hand
          // the 402 back to the caller: its catch would raise a "Failed to start test" alert or
          // an error banner in the moment before the page unloads. A promise that never
          // settles keeps the caller waiting until it does. (On the target page itself no
          // redirect is pending, so the caller gets the 402 as usual.)
          return new Promise(() => {});
        }
      }
      return Promise.reject(error);
    }
  );
}
